import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { AshbyCreateReferralInputSchema } from "@app/lib/api/actions/servers/ashby/types";
import { z } from "zod";

const DEFAULT_SEARCH_LIMIT = 20;
export const GET_REFERRAL_FORM_TOOL_NAME = "get_referral_form";
export const CREATE_REFERRAL_TOOL_NAME = "create_referral";
export const UPDATE_JOB_POSTING_TOOL_NAME = "update_job_posting";

const CandidateSearchSchema = {
  email: z
    .string()
    .optional()
    .describe("Email address to search for (partial matches supported)."),
  name: z
    .string()
    .optional()
    .describe("Name to search for (partial matches supported)."),
};

export const ASHBY_TOOLS_METADATA = [
  {
    name: "search_candidates",
    description:
      "Find, search, and look up candidates in Ashby by name and/or email. " +
      `Returns up to ${DEFAULT_SEARCH_LIMIT} matching candidates by default.`,
    schema: CandidateSearchSchema,
    stake: "never_ask",
    displayLabels: {
      running: "Searching candidates on Ashby",
      done: "Search candidates on Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_report_data",
    description: "Retrieve report data and save it as a CSV file.",
    schema: {
      reportUrl: z
        .string()
        .describe(
          "Full URL of the Ashby report (e.g., https://app.ashbyhq.com/reports/saved/[reportId])."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving Ashby report data",
      done: "Retrieve Ashby report data",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_interview_feedback",
    description:
      "Retrieve interview feedback for a candidate. " +
      "This tool will search for the candidate by name or email and return all submitted " +
      "interview feedback for the most recent application.",
    schema: CandidateSearchSchema,
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving interview feedback from Ashby",
      done: "Retrieve interview feedback from Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_candidate_notes",
    description:
      "Retrieve and read all existing notes recorded on a candidate's profile in Ashby. " +
      "Searches for the candidate by name or email and returns every note already on their profile.",
    schema: CandidateSearchSchema,
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving candidate notes from Ashby",
      done: "Retrieve candidate notes from Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_openings",
    description:
      "List openings in Ashby. Returns a paginated page of openings with " +
      "their state, archive status, latest version details, linked jobs, " +
      "hiring team, and custom fields.",
    schema: {
      cursor: z
        .string()
        .optional()
        .describe(
          "Opaque cursor returned by a previous list_openings call. " +
            "Use this to retrieve the next page."
        ),
      limit: z
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe(
          "Maximum number of openings to return. Defaults to 100, which is also Ashby's maximum."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing openings from Ashby",
      done: "List openings from Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "create_candidate_note",
    description:
      "Create and add a new note to a candidate's profile in Ashby. " +
      "The note content can include basic HTML formatting (supported tags: h1-h6, p, b, i, u, a, ul, ol, li, code, pre).",
    schema: {
      ...CandidateSearchSchema,
      noteContent: z
        .string()
        .describe("The content of the note in HTML format."),
    },
    stake: "high",
    displayLabels: {
      running: "Creating candidate note on Ashby",
      done: "Create candidate note on Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: GET_REFERRAL_FORM_TOOL_NAME,
    description:
      "Retrieve the referral form definition from Ashby. " +
      "Returns all form fields with their titles, types, and whether they are required. " +
      "You must call this tool before creating a referral to know the available fields.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving referral form from Ashby",
      done: "Retrieve referral form from Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: CREATE_REFERRAL_TOOL_NAME,
    description:
      "Create a referral for a candidate in Ashby. " +
      `You must call ${GET_REFERRAL_FORM_TOOL_NAME} first to know the available fields. ` +
      "The credited user is resolved automatically from the authenticated user. " +
      "Field values must be provided as {title, value} pairs where title is " +
      `the human-readable field title exactly as returned by ${GET_REFERRAL_FORM_TOOL_NAME}.`,
    schema: AshbyCreateReferralInputSchema.shape,
    stake: "high",
    displayLabels: {
      running: "Creating referral on Ashby",
      done: "Create referral on Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_job_postings",
    description:
      "List all published job postings in Ashby. " +
      "Returns job postings with their title, department, team, location, " +
      "employment type, workplace type, and other details. " +
      "By default includes both listed and unlisted postings. " +
      "Set listedOnly to true to only return publicly listed postings.",
    schema: {
      location: z
        .string()
        .optional()
        .describe("Filter by location name (case sensitive)."),
      department: z
        .string()
        .optional()
        .describe("Filter by department name (case sensitive)."),
      listedOnly: z
        .boolean()
        .optional()
        .describe(
          "If true, only return publicly listed job postings. " +
            "Defaults to false."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing job postings from Ashby",
      done: "List job postings from Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_hire_data",
    description:
      "Retrieve comprehensive data for a hired candidate, including candidate details, " +
      "offer form fields (both standard and custom), job information, and application data. " +
      "This tool finds the candidate's hired application and returns all available information " +
      "including: name, email, phone, location/country, offer details (start date, compensation, " +
      "custom fields like middle name, preferred name, address, supervisor, employee type, " +
      "scheduled hours, etc.), and job details (department, team, job code).",
    schema: CandidateSearchSchema,
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving hire data from Ashby",
      done: "Retrieve hire data from Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "update_job_posting",
    description:
      "Update an existing job posting in Ashby. " +
      "You can update the title, description, and/or workplace type. " +
      "The description must be in HTML format " +
      "(supported tags: h1-h6, b, i, u, a, ul, ol, li, code, pre). " +
      "When description openings and closings are not suppressed, only the " +
      "descriptionBody part is modifiable. To fully customize the description, " +
      "set both suppressDescriptionOpening and suppressDescriptionClosing to true.",
    schema: {
      jobPostingId: z.string().describe("The ID of the job posting to update."),
      jobId: z.string().describe("The ID of the parent job for this posting."),
      title: z.string().optional().describe("A new title for the job posting."),
      descriptionHtml: z
        .string()
        .optional()
        .describe(
          "Updated HTML description for the job posting. " +
            "Only the descriptionBody part is modifiable unless " +
            "both suppressDescriptionOpening and suppressDescriptionClosing are true."
        ),
      workplaceType: z
        .enum(["OnSite", "Hybrid", "Remote"])
        .optional()
        .describe(
          "The type of workplace for the job posting: " +
            "OnSite, Hybrid, or Remote."
        ),
      suppressDescriptionOpening: z
        .boolean()
        .optional()
        .describe(
          "When true, the job description opening from the brand " +
            "will be suppressed."
        ),
      suppressDescriptionClosing: z
        .boolean()
        .optional()
        .describe(
          "When true, the job description closing from the brand " +
            "will be suppressed."
        ),
    },
    stake: "high",
    displayLabels: {
      running: "Updating job posting on Ashby",
      done: "Update job posting on Ashby",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const ASHBY_SERVER = {
  serverInfo: {
    name: "ashby",
    version: "1.0.0",
    description:
      "Access and manage Ashby ATS (applicant tracking system) data for recruiting: candidates, job postings, interview feedback, referrals, and hiring.",
    authorization: null,
    icon: "AshbyLogo",
    documentationUrl: "https://docs.dust.tt/docs/ashby-mcp",
  },
  tools: ASHBY_TOOLS_METADATA,
} as const satisfies ServerMetadata;
